if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // 【新增】绑定移动端圆点点击大图跳转事件
        const dotsContainer = this.querySelector('.slider-dots-container');
        if (dotsContainer) {
          dotsContainer.querySelectorAll('.slider-dot').forEach((dot) => {
            dot.addEventListener('click', () => {
              const index = parseInt(dot.dataset.index) - 1;
              const activeMedia = this.elements.viewer.querySelectorAll('[data-media-id]')[index];
              if (activeMedia) {
                this.preventSlideChanged = true; // 锁定滚动条的自动事件反馈，防止跳动
                this.elements.viewer.slider.scrollTo({
                  left: activeMedia.offsetLeft,
                  behavior: 'smooth'
                });
                dotsContainer.querySelectorAll('.slider-dot').forEach(d => d.classList.remove('is-active'));
                dot.classList.add('is-active');
              }
            });
          });
        }

        // 【新增】移动端无缝循环滑动：首张←→末张互相衔接
        this._initInfiniteLoop();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          btn.addEventListener('mouseenter', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /**
       * 无缝循环滑动：检测触摸方向，在边界处跳转到对侧。
       * 原理：touchstart 记录起点，touchend 判断划动方向；
       *       若在首张向右划（请求上一张），瞬间跳至末张；
       *       若在末张向左划（请求下一张），瞬间跳至首张。
       */
      _initInfiniteLoop() {
        const slider = this.elements.viewer;
        if (!slider) return;

        let touchStartX = 0;

        slider.addEventListener('touchstart', (e) => {
          touchStartX = e.touches[0].clientX;
        }, { passive: true });

        slider.addEventListener('touchend', (e) => {
          // 仅在移动端（非平板/桌面）生效
          if (this.mql.matches) return;

          const deltaX = e.changedTouches[0].clientX - touchStartX;
          // 阈值 20px，过滤误触
          if (Math.abs(deltaX) < 20) return;

          const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
          if (slides.length < 2) return;

          // 通过当前激活圆点找到当前索引
          const dotsContainer = this.querySelector('.slider-dots-container');
          const dots = dotsContainer ? Array.from(dotsContainer.querySelectorAll('.slider-dot')) : [];
          const activeIndex = dots.findIndex(d => d.classList.contains('is-active'));
          const total = slides.length;

          const swipedLeft = deltaX < 0;  // 向左划 = 请求下一张
          const swipedRight = deltaX > 0; // 向右划 = 请求上一张

          if (swipedLeft && activeIndex === total - 1) {
            // 末张 → 跳首张
            this._jumpToSlide(0, slides, dots);
          } else if (swipedRight && activeIndex === 0) {
            // 首张 → 跳末张
            this._jumpToSlide(total - 1, slides, dots);
          }
        }, { passive: true });
      }

      /**
       * 瞬间跳转到指定索引的幻灯片（behavior: 'instant'，无动画，产生循环衔接感）
       */
      _jumpToSlide(index, slides, dots) {
        const target = slides[index];
        if (!target) return;

        this.preventSlideChanged = true;
        this.elements.viewer.slider.scrollTo({
          left: target.offsetLeft,
          behavior: 'instant'
        });

        // 同步圆点高亮
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));

        // 同步缩略图高亮（如有）
        const mediaId = target.dataset.mediaId;
        if (this.elements.thumbnails && mediaId) {
          const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumb) this.setActiveThumbnail(thumb);
        }
      }

      onSlideChanged(event) {
        if (this.preventSlideChanged) {
          this.preventSlideChanged = false;
          return;
        }

        // 【新增】实时更新移动端圆点指示器的高亮状态
        const dotsContainer = this.querySelector('.slider-dots-container');
        if (dotsContainer) {
          dotsContainer.querySelectorAll('.slider-dot').forEach((dot, index) => {
            if (index === event.detail.currentPage - 1) {
              dot.classList.add('is-active');
            } else {
              dot.classList.remove('is-active');
            }
          });
        }

        const thumbnail = this.elements.thumbnails ? this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        ) : null;
        if (thumbnail) {
          this.setActiveThumbnail(thumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        this.preventSlideChanged = true; // 开启事件锁，杜绝点击小图时大图平滑滚动触发的二次跳动与延迟
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
